import React from 'react';

import cs from 'classnames';
import get from 'lodash/get';

import {getCommonDocument} from 'app/components/general/GenericDocumentActionButtons/SignoffRequestModal/utils';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {StyledModalFooter} from 'app/components/general/TabbedModal';
import {
  signoffRequest,
  type SignoffRequestPayloadType,
} from 'app/general/actions';
import {BaseActionableDocumentType} from 'app/general/types';
import StateButton from 'app/general/widgets/StateButton';
import {sweetAPI} from 'app/store/sweet-api';
import {useNotifications} from 'app/toasts/hooks';
import {gt} from 'app/utils/i18n';
import {useAsyncActions} from 'app/utils/redux';
import {SIGNOFF_TAGS} from 'app/utils/rtk-tags';

type Props = {
  contentType: 'procedure' | 'policy' | 'proceduregroup';
  document: BaseActionableDocumentType;
  onClose: () => void;
  onSuccess: (doc?: BaseActionableDocumentType) => unknown;
  status: 'start' | 'success' | 'error';
  disabled: boolean;
  onError: () => void;
  onBadRequest: (err: any) => void;
};

export function SignoffFooterPanel({
  document,
  contentType,
  status,
  onClose,
  onSuccess,
  onError,
  onBadRequest,
  disabled,
}: Props) {
  const actions = useAsyncActions({
    signoffRequest,
    invalidateTags: sweetAPI.util.invalidateTags,
  });
  const {showToastNotification} = useNotifications();

  const generateSignoff = (e: React.MouseEvent) => {
    e.preventDefault();
    if (disabled || !document.can_edit) {
      return;
    }
    const payload: SignoffRequestPayloadType = {
      contentType,
      current_version: getCommonDocument(document).current_version,
    };
    return actions.signoffRequest(payload).then(
      () => {
        onSuccess(document);
        onClose();
        showToastNotification(gt('Sign Off request is being created.'), {
          level: 'success',
        });

        // We need to invalidate signoff cache whenever signoff is requested.
        // We will remove this once we rtkfy this component.
        actions.invalidateTags([
          // @ts-expect-error we inject tags on the API after it's created so TS doesn't know bout them
          {type: SIGNOFF_TAGS.TAGS, id: 'list'},
        ]);
      },
      (err) => {
        if (get(err, 'response.status') === 400) {
          onBadRequest(err);
        } else {
          onError();
        }

        return err;
      }
    );
  };

  return (
    <StyledModalFooter>
      <button
        onClick={onClose}
        type="button"
        className="btn btn-link text-secondary"
      >
        <i className="fa fa-times mr-1" />
        {status === 'start' ? gt('Cancel') : gt('Close')}
      </button>
      {status === 'start' && (
        <LazyTooltip
          hide={!disabled}
          tooltip={gt('At least one team must have Sign Offs enabled.')}
        >
          <StateButton
            className={cs('btn btn-primary', {
              disabled: disabled || !document.can_edit,
            })}
            onClick={(e) => generateSignoff(e as React.MouseEvent)}
            busyText={gt('Requesting Sign Off...')}
          >
            {gt('Request Sign Off')}
          </StateButton>
        </LazyTooltip>
      )}
    </StyledModalFooter>
  );
}
